import { useState, type FormEvent } from "react";
import type { AssetDepreciationScheduleOut, AssetOut, DepreciationMethod } from "@shop-ledger/shared-types";
import { Money, asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, Modal, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createAsset, deleteAsset, getDepreciationSchedule, listAssets } from "../../api/assetsApi";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — there is no tenant-settings
// endpoint this app can read it from yet, same documented stand-in `TillPage.tsx` uses.

const DEPRECIATION_METHODS: DepreciationMethod[] = ["STRAIGHT_LINE", "DECLINING_BALANCE"];

/**
 * Fixed assets & depreciation (system_architecture.md §6.6) — a straightforward list +
 * create form + straight-line/declining-balance schedule view, reusing the same
 * `DataTable`/form patterns as `ChartOfAccountsPage`. Was a labeled "Coming soon"
 * placeholder until `POST/GET/PATCH/DELETE /assets` shipped (see git history on this file
 * for that placeholder's own explanation of the gap it was waiting on).
 */
export function AssetsPage() {
  const { ensureFreshToken } = useAuth();
  const assetsState = useAsyncData(() => ensureFreshToken().then((token) => listAssets(token)), []);

  const [branchId, setBranchId] = useState("");
  const [name, setName] = useState("");
  const [purchaseCost, setPurchaseCost] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [method, setMethod] = useState<DepreciationMethod>("STRAIGHT_LINE");
  const [rateAnnual, setRateAnnual] = useState("20.00");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [scheduleAsset, setScheduleAsset] = useState<AssetOut | null>(null);
  const [schedule, setSchedule] = useState<AssetDepreciationScheduleOut | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createAsset(token, {
        branch_id: branchId || null,
        name,
        purchase_cost: asDecimalString(purchaseCost),
        purchase_date: purchaseDate,
        depreciation_method: method,
        depreciation_rate_annual: asDecimalString(rateAnnual),
      });
      setName("");
      setPurchaseCost("");
      setPurchaseDate("");
      setMethod("STRAIGHT_LINE");
      setRateAnnual("20.00");
      assetsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create asset.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleViewSchedule(asset: AssetOut) {
    setScheduleAsset(asset);
    setSchedule(null);
    setScheduleError(null);
    setScheduleLoading(true);
    try {
      const token = await ensureFreshToken();
      setSchedule(await getDepreciationSchedule(token, asset.id));
    } catch (error) {
      setScheduleError(error instanceof ApiError ? error.message : "Could not load depreciation schedule.");
    } finally {
      setScheduleLoading(false);
    }
  }

  async function handleDelete(asset: AssetOut) {
    if (!window.confirm(`Remove "${asset.name}" from fixed assets? This can't be undone.`)) return;
    setDeletingId(asset.id);
    try {
      const token = await ensureFreshToken();
      await deleteAsset(token, asset.id);
      assetsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not remove asset.");
    } finally {
      setDeletingId(null);
    }
  }

  const columns: DataTableColumn<AssetOut, keyof AssetOut | "actions">[] = [
    { key: "name", header: "Name", render: (a) => a.name },
    { key: "purchase_date", header: "Purchased", render: (a) => a.purchase_date },
    { key: "purchase_cost", header: "Purchase cost", align: "right", render: (a) => Money.format(a.purchase_cost, TENANT_CURRENCY_FALLBACK) },
    { key: "current_value", header: "Book value today", align: "right", render: (a) => Money.format(a.current_value, TENANT_CURRENCY_FALLBACK) },
    {
      key: "depreciation_method",
      header: "Method",
      render: (a) => (a.depreciation_method === "DECLINING_BALANCE" ? "Declining balance" : "Straight line"),
    },
    {
      key: "actions",
      header: "",
      render: (a) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button type="button" variant="secondary" size="md" onClick={() => void handleViewSchedule(a)}>
            Schedule
          </Button>
          <Button type="button" variant="ghost" size="md" disabled={deletingId === a.id} onClick={() => void handleDelete(a)}>
            {deletingId === a.id ? "Removing…" : "Remove"}
          </Button>
        </div>
      ),
    },
  ];

  const scheduleColumns: DataTableColumn<AssetDepreciationScheduleOut["rows"][number], keyof AssetDepreciationScheduleOut["rows"][number]>[] = [
    { key: "period_end", header: "Year ending", render: (r) => r.period_end },
    { key: "depreciation_expense", header: "Expense", align: "right", render: (r) => Money.format(r.depreciation_expense, TENANT_CURRENCY_FALLBACK) },
    {
      key: "accumulated_depreciation",
      header: "Accumulated",
      align: "right",
      render: (r) => Money.format(r.accumulated_depreciation, TENANT_CURRENCY_FALLBACK),
    },
    { key: "book_value", header: "Book value", align: "right", render: (r) => Money.format(r.book_value, TENANT_CURRENCY_FALLBACK) },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Fixed Assets &amp; Depreciation</h1>
          <p className="page__subtitle">Equipment, vehicles, and other capital purchases, with a straight-line or declining-balance depreciation schedule.</p>
        </div>
      </div>

      {assetsState.error ? <StatusBanner kind="error">{assetsState.error}</StatusBanner> : null}
      {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Add an asset</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Delivery van" required />
            <BranchSelect id="asset-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="No specific branch" />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="asset-purchase-cost">
                Purchase cost
              </label>
              <input
                id="asset-purchase-cost"
                className="sl-input"
                type="number"
                min="0.01"
                step="0.01"
                value={purchaseCost}
                onChange={(e) => setPurchaseCost(e.target.value)}
                required
              />
            </div>
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="asset-purchase-date">
                Purchase date
              </label>
              <input
                id="asset-purchase-date"
                className="sl-input"
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
              />
            </div>
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="asset-method">
                Depreciation method
              </label>
              <select
                id="asset-method"
                className="sl-input"
                value={method}
                onChange={(e) => setMethod(e.target.value as DepreciationMethod)}
              >
                {DEPRECIATION_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m === "DECLINING_BALANCE" ? "Declining balance" : "Straight line"}
                  </option>
                ))}
              </select>
            </div>
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="asset-rate">
                Annual rate (%)
              </label>
              <input
                id="asset-rate"
                className="sl-input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={rateAnnual}
                onChange={(e) => setRateAnnual(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Adding…" : "Add asset"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        {assetsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={assetsState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No fixed assets recorded yet."
            caption="Fixed Assets"
          />
        )}
      </div>

      <Modal open={scheduleAsset !== null} onClose={() => setScheduleAsset(null)} title={scheduleAsset ? `Depreciation schedule — ${scheduleAsset.name}` : undefined}>
        {scheduleError ? <StatusBanner kind="error">{scheduleError}</StatusBanner> : null}
        {scheduleLoading ? (
          <p className="muted">Loading…</p>
        ) : schedule && schedule.rows.length > 0 ? (
          <DataTable columns={scheduleColumns} rows={schedule.rows} getRowId={(row) => row.period_end} caption="Depreciation schedule" />
        ) : schedule ? (
          <p className="muted">No depreciation rate set for this asset, so no schedule can be computed.</p>
        ) : null}
      </Modal>
    </div>
  );
}
